import { Text, TextLink } from "@idg-web-platform/pulse";
import { useEffect, useRef, useState } from "preact/hooks";
import css from "./orderSummary.module.css";
import { ImageInAspectRatio } from "../components/imageInAspectRatio/imageInAspectRatio.tsx";
import { Status } from "./statusBadge.tsx";
import { formatAmount, formatDate } from "../utils/formats.ts";
import { OrderChannel } from "./entities/OrderChannel.ts";
import { ShipmentStatusRow } from "./components/shipmentStatusRow.tsx";

export interface ShipmentStatus {
  status: Status;
  index: number;
  total: number;
}

export interface OrderSummary {
  id: string;
  detailsLink: string;
  date: Date;
  total: number;
  shipmentStatuses: ShipmentStatus[];
  imageUrls: string[];
  articleNames: string[];
  channel: OrderChannel;
  /** Store name for STATIONARY orders */
  storeName?: string;
  /** Store address for STATIONARY orders */
  storeAddress?: string;
  /** Number of articles in the order */
  articleCount: number;
}

/**
 * Renders an order summary for orders page and last order fragment.
 */
export function renderSummary(item: OrderSummary) {
  return (
    <div>
      <a href={item.detailsLink} className={css.orderSummary}>
        <div className={css.orderMeta}>
          <div className={css.metaItem}>
            <Text sizeStep={0} removeMargin={true} element={"span"}>
              Bestelldatum:
            </Text>
            <Text sizeStep={1} fontStyle={"bold"} removeMargin={true} element={"span"}>
              {formatDate(item.date)}
            </Text>
          </div>
          <div className={css.metaItem}>
            <Text sizeStep={0} removeMargin={true} element={"span"}>
              Bestellnr:
            </Text>
            <Text sizeStep={0} removeMargin={true} element={"span"}>
              {item.id}
            </Text>
          </div>
          <div className={css.metaItem}>
            <Text sizeStep={0} fontStyle={"bold"} removeMargin={true} element={"span"}>
              Gesamt:
            </Text>
            <Text sizeStep={0} fontStyle={"bold"} removeMargin={true} element={"span"}>
              {formatAmount(item.total)}
            </Text>
          </div>
        </div>

        <div className={css.shipmentStatuses}>
          {item.shipmentStatuses.map((shipment, i) => (
            <ShipmentStatusRow key={i} shipment={shipment} />
          ))}
        </div>

        <ProductImages imageUrls={item.imageUrls} articleNames={item.articleNames} />

        <div className={css.orderSummaryFooter}>
          <Text sizeStep={0} removeMargin={true}>
            {item.articleCount} Artikel
          </Text>
          <TextLink sizeStep={0} text={"Bestellung anzeigen"} href={item.detailsLink} />
        </div>
      </a>
    </div>
  );
}

function ProductImages({
  imageUrls,
  articleNames,
}: {
  imageUrls: string[];
  articleNames: string[];
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState({ widthPct: 100, leftPct: 0 });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    function update() {
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const overflows = scrollWidth > clientWidth;
      const widthPct = overflows ? (clientWidth / scrollWidth) * 100 : 100;
      const leftPct = overflows ? (scrollLeft / scrollWidth) * 100 : 0;
      setProgress((prev) =>
        prev.widthPct === widthPct && prev.leftPct === leftPct ? prev : { widthPct, leftPct },
      );
    }

    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro?.disconnect();
    };
  }, []);

  const hasOverflow = progress.widthPct < 100;

  return (
    <div className={css.productImagesWrapper}>
      <div ref={scrollRef} className={css.productImages}>
        {imageUrls.map((imageUrl, index) => (
          <ImageInAspectRatio
            className={css.productImage}
            src={imageUrl}
            alt={articleNames[index] ?? ""}
          />
        ))}
      </div>
      {hasOverflow && (
        <div className={css.scrollTrack}>
          <div
            className={css.scrollThumb}
            style={{ width: `${progress.widthPct}%`, left: `${progress.leftPct}%` }}
          />
        </div>
      )}
    </div>
  );
}
